<template>
  <table class="table-hover reports-table manual-time-logs-table table">
    <tbody
      ref="thead"
      class="thead"
    >
      <tr>
        <th v-if="tableIsTeam">Member</th>
        <th v-if="tableIsTeam && tableShowAboutMember">
          <AppToggleDropdown
            trigger-classes="color-inherit"
            dropdown-classes="table-actions-dropdown"
            extra-classes="member-dropdown-header"
          >
            <template #trigger>
              <span class="clickable hover-visible-container">
                About
                <i class="hi-edit hover-visible text-blue ml-10" />
              </span>
            </template>
            <MembersAboutFields
              :about-fields="aboutFields"
              extra-fields
              custom-fields
              custom-visible-by-default
              @change="handleAboutChange"
            />
          </AppToggleDropdown>
        </th>
        <th>{{ $filters.projectFlavourLabel(organization.project_flavour) }}</th>
        <th>{{ $filters.taskFlavourLabel(organization.project_flavour) }}</th>
        <th>Action</th>
        <th>Time Span</th>
        <th>Time change</th>
        <th>Reason</th>
        <th>Changed by</th>
        <th>Changed at</th>
      </tr>
    </tbody>
    <!-- eslint-disable vue/require-v-for-key -->
    <template v-for="section in tableData.sections">
      <tbody
        ref="groupHeader"
        class="grouping-header"
      >
        <tr>
          <th>Changes made {{ formatDateLabel(section.main) }}</th>
          <td
            v-for="col in numberOfCols()"
            :key="col"
          ></td>
        </tr>
      </tbody>

      <!-- eslint-disable vue/valid-v-for -->
      <ReportVirtualTableBody
        v-slot="{ row }"
        :rows="section.rows"
      >
        <tr>
          <!-- TODO: This cell should have the "sticky-cell" class -->
          <td
            v-if="tableIsTeam"
            class="long-text-cell"
          >
            <ReportTableAvatar
              :user-id="row.record.user_id"
              :url="row.record.avatar_url"
            />
            {{ row.record.user_name }}
          </td>
          <td v-if="tableIsTeam && tableShowAboutMember">
            <MemberAboutData
              :about-fields="aboutFields"
              :member-data="row.record"
              :user-id="row.record.user_id"
              :user-name="row.record.user_name"
              custom-visible-by-default
              @change="handleAboutFieldChange"
            />
          </td>
          <td>
            {{ row.record.project_name }}
            <div
              v-if="row.record.project_id != row.record.original_project_id && row.record.original_project_id"
              class="small text-lighter"
            >
              From: {{ row.record.original_project_name }}
            </div>
          </td>
          <td class="long-text-cell">
            <div
              v-if="row.record.task_summary"
              v-popover:left
              class="manual-time-log--task long-text"
              :data-content="row.record.task_summary"
            >
              {{ row.record.task_summary }}
            </div>
            <div
              v-else
              class="manual-time-log--task text-lighter"
            >
              No {{ $filters.lowercase($filters.taskFlavourLabel(organization.project_flavour)) }}
            </div>
            <div
              v-if="row.record.task_id != row.record.original_task_id && row.record.original_task_id"
              v-popover:left
              class="manual-time-log--original-task small text-lighter long-text"
              :data-content="row.record.original_task_summary"
            >
              From: {{ row.record.original_task_summary }}
            </div>
          </td>
          <td>
            <span :class="actionNameClass(row)">{{ $filters.capitalize(row.record.action_name) }}</span>
          </td>
          <td>
            <span
              v-tooltip
              :title="$filters.formatDatetimeRangeWithZone(row.record.starts_at, row.record.stops_at, true)"
            >
              {{ $filters.formatDatetimeRangeWithZone(row.record.starts_at, row.record.stops_at, false, true) }}
            </span>
          </td>
          <td>
            {{ timeChange(row.record) }}
            <ReportTableTooltipIcon
              :message="timeChangeTooltip(row.record)"
              data-html
              placement="auto top"
              type="question"
            />
          </td>
          <td>
            <span
              v-tooltip
              :title="row.record.reason"
            >
              {{ row.record.reason }}
            </span>
          </td>
          <td>{{ row.record.blame_user_name }}</td>
          <td>
            <span
              v-tooltip
              :title="$filters.formatDateTime(row.record.created_at)"
            >
              {{ $filters.formatTimeWithZone(row.record.created_at, false) }}
            </span>
          </td>
        </tr>
      </ReportVirtualTableBody>
      <!-- eslint-enable vue/valid-v-for -->
    </template>
    <!-- eslint-enable vue/require-v-for-key -->
  </table>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import MemberAboutData from '~/components/members/MemberAboutData.vue';
import MembersAboutFields from '~/components/members/MembersAboutFields.vue';

export default {
  name: 'ManualTimeLogReportTable',
  components: {
    MembersAboutFields,
    MemberAboutData,
  },
  props: {
    tableData: Object,
  },
  emits: ['change'],
  computed: {
    ...mapGetters(['tableIsTeam', 'aboutFields', 'tableShowAboutMember']),
    ...mapState(['organization', 'members']),
  },
  watch: {
    tableIsTeam: {
      immediate: true,
      async handler() {
        await this.$nextTick();
        this.setCellsWidth();
      },
    },
  },
  methods: {
    actionNameClass(row) {
      return 'action action-' + row.record.action_name.toLowerCase().replace(/\s/g, '-');
    },
    /**
     * FIXME: This is the only way that we had to make the cells aligned between different nested tables.
     * Please do not update this without first making sure that the changes you made are compatible with the sizing
     * and the virtualization of the table.
     * Bernat Jufré 2022-10-25
     */
    ...mapActions(['updateAboutFields']),
    setCellsWidth() {
      if (!this.$refs.thead) {
        return;
      }

      const allHeaderRects = [...this.$refs.thead.querySelectorAll('th')].map((h) => h.getBoundingClientRect());
      const allCells = this.$el.querySelectorAll('.tbody-virtual td');

      for (let i = 0; i < allCells.length; i++) {
        const cell = allCells[i];
        const rect = allHeaderRects[i % allHeaderRects.length];

        cell.style = `max-width: ${rect.width}px; min-width: ${rect.width}px;`;
      }
    },
    formatDateLabel(value) {
      if (value === 'summed_date') {
        return (
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date)) +
          ' - ' +
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date_end))
        );
      }

      return TimeUtils.format_date(TimeUtils.parse_internal_date(value));
    },
    timeChange(activity) {
      if (activity.added_seconds > 0) {
        return this.$filters.formatDuration(activity.added_seconds);
      }

      if (activity.replaced_seconds > 0) {
        return this.$filters.formatDuration(activity.replaced_seconds);
      }

      if (activity.deleted_seconds > 0) {
        return this.$filters.formatDuration(activity.deleted_seconds);
      }

      return this.$filters.formatDuration(activity.seconds, null, false, true);
    },
    timeChangeTooltip(activity) {
      const text = [];

      if (activity.added_seconds > 0) {
        text.push('Added: ' + this.$filters.formatDuration(activity.added_seconds));
      }

      if (activity.replaced_seconds > 0) {
        text.push('Replaced: ' + this.$filters.formatDuration(activity.replaced_seconds));
      }

      if (activity.deleted_seconds > 0) {
        text.push('Deleted: ' + this.$filters.formatDuration(activity.deleted_seconds));
      }

      return text.join('<br/>');
    },
    handleAboutFieldChange(payload) {
      this.updateAboutFields({ ...this.aboutFields, ...payload });
    },
    numberOfCols() {
      if (this.tableIsTeam && this.tableShowAboutMember) {
        return 9;
      }
      if (this.tableIsTeam) {
        return 8;
      }

      return 7;
    },
    handleAboutChange(payload) {
      this.$emit('change', payload);
    },
  },
};
</script>
